import { z } from "zod";
import type { ImportKind } from "./imports";
import type { QbExpenseCheckResult, QbExpensePlan, QbInvoiceIssue } from "./qb-expenses";
import type { QbInvoiceCheckResult } from "./invoice-import";
import type { QbLinkCounts, QbMatch, QbUnlinked } from "./quickbooks";

/**
 * Settings → Sync: one button reads JobNimbus and QuickBooks and shows everything it would do; Apply
 * does it all, in order. Nothing changes before Apply.
 */

export type SyncStepStatus = "waiting" | "running" | "done" | "skipped" | "failed";
export interface SyncStep {
  key: string;
  label: string;
  status: SyncStepStatus;
  detail: string;
}

export interface SyncPreview {
  checkedAt: string;
  /** null: JobNimbus isn't read (set up off, or retired). */
  jobnimbus: {
    runId: string;
    /** What adding would bring in, per kind (new and merged; already imported isn't counted). */
    adding: Record<ImportKind, number>;
    /** Records the review flagged (a possible duplicate, a match with someone already here) for a look first. */
    flagged: number;
  } | null;
  /** null: QuickBooks isn't connected. */
  quickbooks: {
    links: QbMatch[];
    expenses: QbExpensePlan;
    /** Jobs, customers, and vendors not found in QuickBooks, each with why. */
    notFound: QbUnlinked[];
    invoiceIssues: QbInvoiceIssue[];
    /** How many of each are linked, ready to link in this review, and not found. */
    counts: SyncLinkCounts;
  } | null;
}

export interface SyncApplied {
  jobnimbus: { contacts: number; jobs: number; notes: number; tasks: number; invoices: number; files: string } | null;
  links: number;
  invoices: QbInvoiceCheckResult | null;
  expenses: QbExpenseCheckResult | null;
}

export interface SyncState {
  phase: "idle" | "checking" | "ready" | "applying" | "done" | "failed";
  steps: SyncStep[];
  preview: SyncPreview | null;
  applied: SyncApplied | null;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface SyncLinkCounts {
  jobs: QbLinkCounts;
  customers: QbLinkCounts;
  vendors: QbLinkCounts;
}

/** The status block at the top of Settings → Sync. */
export interface SyncStatus {
  jobnimbus: {
    setUp: boolean;
    retired: boolean;
    lastPull: string | null;
    lastAdded: string | null;
    /** A pull waiting to be added. */
    waiting: boolean;
    invoicesBroughtIn: number;
  };
  quickbooks: {
    connected: boolean;
    webhooks: boolean;
    lastNotice: string | null;
    lastChanges: string | null;
    lastMorningSync: string | null;
    held: number;
    /** From the last Sync (after its Apply, when there was one). */
    linkCounts: (SyncLinkCounts & { checkedAt: string }) | null;
  };
  warnings: string[];
}

/** What Apply sends back: the links and expense changes the person kept (everything shown, by default). */
export interface SyncApplyInput {
  jobnimbus: boolean;
  links: { kind: QbMatch["kind"]; recordId: string; qbId: string }[];
  expenseKeys: string[];
}

export const syncApplySchema = z.object({
  jobnimbus: z.boolean(),
  links: z.array(z.object({ kind: z.enum(["job", "customer", "vendor"]), recordId: z.string().uuid(), qbId: z.string().min(1).max(40) })).max(5000),
  expenseKeys: z.array(z.string().max(200)).max(20000),
});
